/**
 * The decisions /api/checkout/create-payment-intent makes about money, without
 * the checkout.
 *
 * The handler is a long sequence of I/O — a Zod parse, four Prisma reads, a
 * seller-verification call, a CJ stock probe, a carrier re-quote, a coupon
 * validate-and-redeem, a Stripe PaymentIntent create — and threaded through it
 * are the choices that decide what the buyer is actually charged: whether a
 * VarChar price column holds a usable number, which CJ variant the order is
 * for and what it costs, what the totals and the Stripe fee come to, what a
 * coupon does to those totals, how far the application fee may go before Stripe
 * refuses the charge, whether the price moved since the page rendered, who the
 * redeeming buyer is, and which sentence a refusal produces.
 *
 * Inside the handler every one of those could only be reached by stacking a
 * Prisma mock on a Stripe mock on a session mock and driving a whole request
 * through them, which is why the CJ variant resolution and the amount
 * arithmetic carried the file's largest block of untested branches. Here the
 * same code is called with plain objects and checked against plain numbers.
 *
 * Everything moved verbatim from pages/api/checkout/create-payment-intent.ts,
 * which now calls it. Messages, rounding, defaults and the order of the checks
 * are unchanged — this is a money path, and a difference here is a difference
 * in what a buyer pays. Nothing in this file reads the database, talks to
 * Stripe or CJ, or logs.
 */

import { calculateStripeFeeCents } from '../stripe-fees';
import type { CJProductVariant } from '../../schemas/api/feeds/cjdropshipping';

// ---------------------------------------------------------------------------
// Listing price
// ---------------------------------------------------------------------------

/**
 * SUPPLY_LISTINGS.price is a VarChar(25), so a row can hold '', 'free', or a
 * currency-formatted string. Returns null for anything that is not a usable
 * positive amount rather than letting NaN reach Stripe.
 */
export function parseListingPriceDollars(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const parsed = parseFloat(String(raw).replace(/[$,\s]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// Ids off the listing row
// ---------------------------------------------------------------------------

/**
 * The seller, as the fee-grant resolver wants it: a number, or null when the
 * listing row has no owner. A hard-deleted owner leaves `userId` null on rows
 * that are still listed, so this is a real case and not just column nullability.
 */
export function sellerIdOrNull(value: number | string | null | undefined): number | null {
  return value ? Number(value) : null;
}

/**
 * The same id where the caller wants it absent rather than null — the coupon
 * validator's optional targeting fields, which distinguish "not scoped" from a
 * scope of nobody. Zero reads as absent, which is what `categoryGroupId`'s
 * `@default(0)` means on a listing that was never categorised.
 */
export function optionalNumericId(value: number | string | null | undefined): number | undefined {
  return value ? Number(value) : undefined;
}

// ---------------------------------------------------------------------------
// Where the order ships (for a CJ freight re-quote)
// ---------------------------------------------------------------------------

/** Destination fields the shipping-rate verifier needs to re-quote freight. */
export interface QuoteDestination {
  country: string;
  postalCode: string;
  city?: string;
  state?: string;
}

/** The address shape a guest checkout posts inline. */
export interface GuestShippingAddressLike {
  country?: string | null;
  postal_code?: string | null;
  city?: string | null;
  state?: string | null;
}

/** The ADDRESS columns an authenticated checkout's saved address is read from. */
export interface SavedAddressLike {
  zipCode?: string | null;
  locality?: string | null;
  region?: string | null;
}

/**
 * Guest destination. Null when the buyer has not given a postal code, which
 * only blocks CJ verification — Shippo rates are verifiable from the rate id
 * alone. Country defaults to US because that is the only country the guest
 * form offers, and an empty string would re-quote against no country at all.
 */
export function guestQuoteDestination(
  addr: GuestShippingAddressLike | null | undefined,
): QuoteDestination | null {
  if (!addr?.postal_code) return null;
  return {
    country: addr.country || 'US',
    postalCode: addr.postal_code,
    city: addr.city || undefined,
    state: addr.state || undefined,
  };
}

/**
 * Destination from a saved ADDRESS row. Country is hard-coded US: the column
 * does not exist on the row, and the saved-address form is US-only.
 */
export function savedAddressQuoteDestination(
  address: SavedAddressLike | null | undefined,
): QuoteDestination | null {
  if (!address?.zipCode) return null;
  return {
    country: 'US',
    postalCode: address.zipCode,
    city: address.locality || undefined,
    state: address.region || undefined,
  };
}

// ---------------------------------------------------------------------------
// CJ variant resolution (a security boundary: never trust the posted price)
// ---------------------------------------------------------------------------

export const CJ_VARIANT_NOT_AVAILABLE = 'Selected variant is not available for this listing';
export const CJ_VARIANT_NO_VALID_PRICE = 'Selected variant has no valid price';

/**
 * Which CJ variant the order is for and what it costs.
 *
 * `itemPrice` null means "no variant priced this order" — a no-variant CJ
 * product — and the caller falls back to SUPPLY_LISTINGS.price. A non-null
 * `error` is a refusal: the buyer asked for a variant this listing does not
 * have, or one whose stored price is unusable. Both are 400s rather than a
 * guessed price.
 *
 * One object rather than a discriminated union on purpose — this repo compiles
 * with `strict: false`, which does not narrow a union by a boolean discriminant,
 * so the caller would have to cast to read the message.
 */
export interface CJVariantSelection {
  error: string | null;
  itemPrice: number | null;
  variantVid: string | null;
}

/**
 * The variants stored on LISTINGS_METADATA.externalData.
 *
 * The column is free-form text written by the CJ importer. A blob that is
 * missing, unparseable, or carries something other than a variants array reads
 * as "no variants" rather than throwing — the caller's next move either way is
 * the listing's own price.
 */
export function parseCJVariants(externalData: string | null | undefined): CJProductVariant[] {
  if (!externalData) return [];
  try {
    const parsed = JSON.parse(externalData) as { variants?: CJProductVariant[] };
    return Array.isArray(parsed.variants) ? parsed.variants : [];
  } catch {
    return [];
  }
}

/**
 * A variant's sell price as a number. CJ ships it as either a number or a
 * numeric string depending on which endpoint filled the blob; anything else
 * (null, '', 'FREE') becomes NaN so the caller's finite check refuses it.
 */
export function parseCJVariantSellPrice(raw: CJProductVariant['variantSellPrice']): number {
  return typeof raw === 'number' ? raw : parseFloat(String(raw ?? 'NaN'));
}

/**
 * Resolve the buyer's variant against the stored blob.
 *
 * With a requested vid the match has to exist and carry a usable price. With
 * none, the cheapest priced variant wins — that mirrors ProductProvider's
 * default selection, so the price the buyer saw on the product page is the
 * price they pay.
 */
export function resolveCJVariantSelection(
  externalData: string | null | undefined,
  requestedVid: string | null | undefined,
): CJVariantSelection {
  const variants = parseCJVariants(externalData);

  if (requestedVid && variants.length > 0) {
    const matched = variants.find((v) => v.vid === requestedVid);
    if (!matched) {
      return { error: CJ_VARIANT_NOT_AVAILABLE, itemPrice: null, variantVid: null };
    }
    const matchedPrice = parseCJVariantSellPrice(matched.variantSellPrice);
    if (!Number.isFinite(matchedPrice) || matchedPrice <= 0) {
      return { error: CJ_VARIANT_NO_VALID_PRICE, itemPrice: null, variantVid: null };
    }
    return { error: null, itemPrice: matchedPrice, variantVid: requestedVid };
  }

  if (variants.length > 0) {
    const priced = variants
      .filter((v): v is CJProductVariant & { vid: string } => !!v?.vid)
      .map((v) => ({
        vid: v.vid,
        price: parseCJVariantSellPrice(v.variantSellPrice),
      }))
      .filter((p) => Number.isFinite(p.price) && p.price > 0)
      .sort((a, b) => a.price - b.price);
    if (priced.length > 0) {
      return { error: null, itemPrice: priced[0].price, variantVid: priced[0].vid };
    }
  }

  return { error: null, itemPrice: null, variantVid: null };
}

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

/** What the buyer is charged, in cents, broken out the way metadata records it. */
export interface CheckoutCharge {
  itemPriceCents: number;
  shippingCents: number;
  stripeFeeCents: number;
  totalCents: number;
}

/** Dollars to cents, the one rounding the charge is allowed to do. */
export function toCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * Item + shipping + the Stripe fee on both. The fee is charged on the sum, not
 * on the item alone, because that is what Stripe takes off the charge.
 */
export function computeCheckoutCharge(itemPriceCents: number, shippingCents: number): CheckoutCharge {
  const stripeFeeCents = calculateStripeFeeCents(itemPriceCents + shippingCents);
  return {
    itemPriceCents,
    shippingCents,
    stripeFeeCents,
    totalCents: itemPriceCents + shippingCents + stripeFeeCents,
  };
}

/**
 * The charge after a coupon.
 *
 * A coupon lowers the item, never the shipping, and never below zero — a
 * discount larger than the item is a $0 item plus real freight, not a refund.
 * The Stripe fee is recomputed on the reduced charge, so the buyer is not
 * billed a fee on money they no longer pay.
 */
export function applyBuyerDiscount(
  itemPriceCents: number,
  shippingCents: number,
  discountAmountDollars: number,
): CheckoutCharge & { discountCents: number } {
  const discountCents = toCents(discountAmountDollars);
  const adjustedItemPriceCents = Math.max(0, itemPriceCents - discountCents);
  return {
    discountCents,
    ...computeCheckoutCharge(adjustedItemPriceCents, shippingCents),
  };
}

/**
 * The application fee with the buyer's processing charge added.
 *
 * `commissionFeeCents` is what `calculateCommission` bills: the commission,
 * plus the shipping when Sellwild buys the label. The processing line the
 * buyer paid (2.9% + $0.30) goes to Sellwild as well: on a destination charge
 * Stripe takes its real card fee from the platform balance, so a processing
 * charge that reached the seller's transfer left Sellwild paying the card fee
 * out of its commission on every sale. Product decision 2026-09-25.
 *
 * Sales tax is added later, and the whole is clamped below the charge.
 */
export function applicationFeeWithProcessing(commissionFeeCents: number, processingFeeCents: number): number {
  return commissionFeeCents + Math.max(0, processingFeeCents);
}

/**
 * The application fee once a platform coupon is funded.
 *
 * Who pays a coupon (product decision 2026-09-25): a seller's own coupon comes
 * out of the seller's share, and a platform coupon (one with no `sellerId`) is
 * paid by Sellwild. For a platform coupon the caller bills the commission on
 * the LIST price and passes the discount here, and the fee drops by it, so the
 * seller's transfer is what it would have been without the coupon.
 *
 * A fee cannot go below zero. When the discount is larger than everything
 * Sellwild would have kept, the fee stops at zero and the rest is
 * `platformShortfallCents`: money the seller is owed and the charge cannot
 * carry. The caller records it; nothing pays it automatically.
 */
export function applicationFeeAfterPlatformCoupon(input: {
  /** Commission (+ label shipping) + processing, before the coupon. */
  feeCents: number;
  /** The platform coupon's discount, 0 for a seller coupon or none. */
  platformDiscountCents: number;
}): { applicationFeeCents: number; platformShortfallCents: number } {
  const discount = Math.max(0, input.platformDiscountCents);
  const fee = input.feeCents - discount;
  return fee >= 0
    ? { applicationFeeCents: fee, platformShortfallCents: 0 }
    : { applicationFeeCents: 0, platformShortfallCents: -fee };
}

/**
 * How far the application fee may go.
 *
 * Stripe rejects an application fee that is not smaller than the charge, so a
 * deep discount used to fail every checkout on the listing outright: the fee
 * was computed once from the undiscounted price and passed through unchanged
 * while the charge shrank. Commission still bills on the original price by
 * design — this only stops it exceeding what was actually collected.
 */
export function clampApplicationFee(
  applicationFee: number,
  chargeCents: number,
): { applicationFee: number; ceiling: number; clamped: boolean } {
  const ceiling = Math.max(0, chargeCents - 1);
  const clamped = applicationFee > ceiling;
  return { applicationFee: clamped ? ceiling : applicationFee, ceiling, clamped };
}

// ---------------------------------------------------------------------------
// What the client said the price was
// ---------------------------------------------------------------------------

/**
 * The price the product page rendered, in cents, or null when the body carried
 * nothing usable. It is display context only — never the charge — but it is
 * the one record of what the buyer was shown.
 */
export function claimedPriceCents(price: number | null | undefined): number | null {
  return typeof price === 'number' && Number.isFinite(price) ? toCents(price) : null;
}

/**
 * Worth an analytics event: the gap between what the client displayed and what
 * we will bill. A small delta is ordinary (the seller edited the price, or an
 * auto-apply discount was baked into the page); a large or negative one is
 * worth seeing.
 */
export function claimedPriceDiffers(claimedCents: number | null, chargedCents: number): boolean {
  return claimedCents !== null && claimedCents !== chargedCents;
}

/**
 * Never bill more than the buyer was shown. If the amount we are about to
 * charge exceeds the figure the page rendered, the coupon that produced that
 * figure is gone (or the seller raised the price) and the buyer has to see the
 * new number before anything is billed.
 */
export function exceedsShownPrice(claimedCents: number | null, chargedCents: number): boolean {
  return claimedCents !== null && chargedCents > claimedCents;
}

// ---------------------------------------------------------------------------
// Who is redeeming
// ---------------------------------------------------------------------------

/** The parts of the NextAuth session this endpoint reads for an id. */
export interface CheckoutSessionLike {
  profile?: { id?: string | number | null } | null;
  user?: { id?: string | number | null } | null;
}

/**
 * The redeeming buyer, or undefined.
 *
 * Guests — guest mode, or an authenticated session with no resolvable id — are
 * represented as undefined and NOT as a placeholder buyer id, so the coupon
 * validator/redeemer skips the per-user redemption check instead of scoping it
 * to a shared account (which falsely tripped USER_LIMIT_REACHED and leaked one
 * guest's usage onto another). `profile.id` is the database id and wins over
 * `user.id`, which can be a provider id.
 */
export function sessionCouponUserId(session: CheckoutSessionLike | null | undefined): number | undefined {
  if (!session) return undefined;
  const sessionUserId = Number(session?.profile?.id || session?.user?.id);
  return Number.isInteger(sessionUserId) && sessionUserId > 0 ? sessionUserId : undefined;
}

// ---------------------------------------------------------------------------
// Coupon metadata and refusal text
// ---------------------------------------------------------------------------

/** The validated coupon fields that reach PaymentIntent metadata. */
export interface AppliedCouponLike {
  couponId?: number | null;
  code?: string | null;
  name: string;
  couponType: string;
  discountAmount: number;
}

/**
 * The coupon block on the PaymentIntent, which is the only record of why the
 * charge was lower than the listing price by the time anyone reconciles it.
 * `couponSource` distinguishes a code the buyer typed from the auto-apply
 * coupon the server re-derived.
 */
export function buildCouponMetadata(
  coupon: AppliedCouponLike,
  couponSource: 'explicit' | 'auto',
): Record<string, string> {
  return {
    couponId: String(coupon.couponId),
    couponCode: coupon.code || '',
    couponName: coupon.name,
    couponType: coupon.couponType,
    couponDiscountAmount: coupon.discountAmount.toFixed(2),
    couponSource,
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * A thrown value as the two fields the trackers take. A rejection is not always
 * an Error — CJ's client and the analytics writer both reject with strings on
 * some paths — and `String(err)` beats an empty message field.
 */
export function describeUnknownError(err: unknown): { message: string; stack: string | undefined } {
  return {
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
  };
}

/** The error shape Stripe's SDK throws, as this handler's catch block reads it. */
export interface StripeLikeError {
  message?: string;
  type?: string;
  code?: string;
  stack?: string;
  raw?: { type?: string; code?: string };
}

/**
 * Whether the failure came from Stripe rather than from us.
 *
 * The SDK sets `type` to a `Stripe*` name on its own errors, but a raw API
 * refusal that never got wrapped carries the type on `raw` instead — matching
 * only the first filed real card declines as application faults.
 */
export function isStripeApiError(err: StripeLikeError): boolean {
  return !!(err.type?.startsWith('Stripe') || err.raw?.type);
}

/** Stripe's own error code, wherever the SDK put it. */
export function stripeErrorCode(err: StripeLikeError): string | undefined {
  return err.code || err.raw?.code;
}

/** Stripe's own error type, wherever the SDK put it; 'unknown' if neither. */
export function stripeErrorType(err: StripeLikeError): string {
  return err.type || err.raw?.type || 'unknown';
}
